import { downloadQuadraticFile } from '@/app/helpers/downloadFileInBrowser';
import { ApiError, fetchFromApi } from '@/shared/api/fetchFromApi';
import { xhrFromApi } from '@/shared/api/xhrFromApi';
import { trackEvent } from '@/shared/utils/analyticsEvents';
import { getUtmDataFromCookie } from '@/shared/utils/getUtmDataFromCookie';
import { captureEvent } from '@sentry/react';
import { Buffer } from 'buffer';
import { ApiSchemas, type ApiTypes } from 'quadratic-shared/typesAndSchemas';

// Shared query shapes for the paginated file list endpoints. See the API
// route files for full semantics:
//   GET /v0/teams/:uuid/files          → folder-scoped list (Endpoint 2)
//   GET /v0/teams/:uuid/files/recent   → cross-team recent (Endpoint 1)

export type FileListFilters = {
  search?: string;
  hasScheduledTasks?: boolean;
  sharedPublicly?: boolean;
  creatorUserIds?: number[];
};

export type PaginatedRecentFilesQuery = FileListFilters & {
  fileType?: 'team' | 'private' | 'shared';
  sort?: 'updated' | 'created' | 'name';
  order?: 'asc' | 'desc';
  cursor?: string;
  limit?: number;
};

export type PaginatedFolderFilesQuery = FileListFilters & {
  scope: 'team-root' | 'private-root' | 'folder';
  folderUuid?: string;
  sort?: 'updated' | 'created' | 'name';
  order?: 'asc' | 'desc';
  cursor?: string;
  limit?: number;
};

const buildFileListSearchParams = (params: PaginatedFolderFilesQuery | PaginatedRecentFilesQuery): URLSearchParams => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      sp.set(key, value.join(','));
    } else if (typeof value === 'boolean') {
      sp.set(key, value ? 'true' : 'false');
    } else {
      sp.set(key, String(value));
    }
  });
  return sp;
};

export const apiClient = {
  teams: {
    list() {
      return fetchFromApi(`/v0/teams`, { method: 'GET' }, ApiSchemas['/v0/teams.GET.response']);
    },
    async get(uuid: string, options?: { updateBilling?: boolean }) {
      const queryParams = new URLSearchParams();
      if (options?.updateBilling) {
        queryParams.set('updateBilling', 'true');
      }
      const queryString = queryParams.toString();
      const url = `/v0/teams/${uuid}${queryString ? `?${queryString}` : ''}`;
      const response = await fetchFromApi(url, { method: 'GET' }, ApiSchemas['/v0/teams/:uuid.GET.response']);

      if (response.license.status === 'revoked') {
        throw new ApiError('License Revoked', 402, undefined);
      }

      return response;
    },
    update(uuid: string, body: ApiTypes['/v0/teams/:uuid.PATCH.request']) {
      return fetchFromApi(
        `/v0/teams/${uuid}`,
        { method: 'PATCH', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid.PATCH.response']
      );
    },
    create() {
      return fetchFromApi(
        `/v0/teams`,
        { method: 'POST', body: JSON.stringify({ name: 'My Team' }) },
        ApiSchemas['/v0/teams.POST.response']
      );
    },
    billing: {
      getPortalSessionUrl(uuid: string) {
        return fetchFromApi(
          `/v0/teams/${uuid}/billing/portal/session`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/billing/portal/session.GET.response']
        );
      },
      getCheckoutSessionUrl(
        uuid: string,
        redirectUrlSuccess: string,
        redirectUrlCancel: string,
        plan?: 'pro' | 'business'
      ) {
        const queryParams = new URLSearchParams({
          'redirect-success': redirectUrlSuccess,
          'redirect-cancel': redirectUrlCancel,
        });
        if (plan) {
          queryParams.set('plan', plan);
        }
        const { gclid } = getUtmDataFromCookie();
        if (gclid) {
          queryParams.set('gclid', gclid);
        }
        return fetchFromApi(
          `/v0/teams/${uuid}/billing/checkout/session?${queryParams.toString()}`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/billing/checkout/session.GET.response']
        );
      },
      getCheckoutSessionAmount(uuid: string, sessionId: string) {
        return fetchFromApi(
          `/v0/teams/${uuid}/billing/checkout/session/${sessionId}`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/billing/checkout/session/:sessionId.GET.response']
        );
      },
      retentionDiscount: {
        async get(uuid: string) {
          return fetchFromApi(
            `/v0/teams/${uuid}/billing/retention-discount`,
            { method: 'GET' },
            ApiSchemas['/v0/teams/:uuid/billing/retention-discount.GET.response']
          );
        },
        async create(uuid: string) {
          return fetchFromApi(
            `/v0/teams/${uuid}/billing/retention-discount`,
            { method: 'POST' },
            ApiSchemas['/v0/teams/:uuid/billing/retention-discount.POST.response']
          );
        },
      },
      async reactivateSubscription(uuid: string) {
        return fetchFromApi(
          `/v0/teams/${uuid}/billing/reactivate`,
          { method: 'POST' },
          ApiSchemas['/v0/teams/:uuid/billing/reactivate.POST.response']
        );
      },
      async cancelScheduledDowngrade(uuid: string) {
        return fetchFromApi(
          `/v0/teams/${uuid}/billing/cancel-scheduled-downgrade`,
          { method: 'POST' },
          ApiSchemas['/v0/teams/:uuid/billing/cancel-scheduled-downgrade.POST.response']
        );
      },
      async aiUsage(uuid: string) {
        const data = await fetchFromApi(
          `/v0/teams/${uuid}/billing/ai/usage`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/billing/ai/usage.GET.response']
        );

        return data;
      },
      async aiUsageUsers(uuid: string) {
        const data = await fetchFromApi(
          `/v0/teams/${uuid}/billing/ai/usage/users`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/billing/ai/usage/users.GET.response']
        );

        return data;
      },
      async aiUsageDaily(uuid: string) {
        const data = await fetchFromApi(
          `/v0/teams/${uuid}/billing/ai/usage/daily`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/billing/ai/usage/daily.GET.response']
        );

        return data;
      },
      async aiUsageYearly(uuid: string) {
        const data = await fetchFromApi(
          `/v0/teams/${uuid}/billing/ai/usage/yearly`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/billing/ai/usage/yearly.GET.response']
        );

        return data;
      },
      async updateOverage(uuid: string, allowOveragePayments: boolean) {
        return fetchFromApi(
          `/v0/teams/${uuid}/billing/overage`,
          { method: 'PATCH', body: JSON.stringify({ allowOveragePayments }) },
          ApiSchemas['/v0/teams/:uuid/billing/overage.PATCH.response']
        );
      },
      async updateBudget(uuid: string, teamMonthlyBudgetLimit: number) {
        return fetchFromApi(
          `/v0/teams/${uuid}/billing/budget`,
          { method: 'PATCH', body: JSON.stringify({ teamMonthlyBudgetLimit }) },
          ApiSchemas['/v0/teams/:uuid/billing/budget.PATCH.response']
        );
      },
      async invoices(uuid: string) {
        return fetchFromApi(
          `/v0/teams/${uuid}/billing/invoices`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/billing/invoices.GET.response']
        );
      },
    },
    files: {
      // Folder-scoped paginated file list. The dashboard uses this for the team
      // root, private root, and folder views. Server-side search/sort/filters.
      list(teamUuid: string, params: PaginatedFolderFilesQuery) {
        const url = `/v0/teams/${teamUuid}/files?${buildFileListSearchParams(params).toString()}`;
        return fetchFromApi(url, { method: 'GET' }, ApiSchemas['/v0/teams/:uuid/files.GET.response']);
      },
      selectionManifest(teamUuid: string, params: PaginatedFolderFilesQuery) {
        const { cursor: _c, limit: _l, ...body } = params;
        return fetchFromApi(
          `/v0/teams/${teamUuid}/files/selection-manifest`,
          { method: 'POST', body: JSON.stringify(body) },
          ApiSchemas['/v0/teams/:uuid/files/selection-manifest.POST.response']
        );
      },
      // Recent files: cross-team union (active team + cross-team shared-with-me).
      // Always sorted by updatedDate desc.
      recent: {
        list(teamUuid: string, params: PaginatedRecentFilesQuery) {
          const url = `/v0/teams/${teamUuid}/files/recent?${buildFileListSearchParams(params).toString()}`;
          return fetchFromApi(url, { method: 'GET' }, ApiSchemas['/v0/teams/:uuid/files/recent.GET.response']);
        },
        selectionManifest(teamUuid: string, params: PaginatedRecentFilesQuery) {
          const { cursor: _c, limit: _l, ...body } = params;
          return fetchFromApi(
            `/v0/teams/${teamUuid}/files/recent/selection-manifest`,
            { method: 'POST', body: JSON.stringify(body) },
            ApiSchemas['/v0/teams/:uuid/files/recent/selection-manifest.POST.response']
          );
        },
      },
      deleted: {
        list(teamUuid: string) {
          return fetchFromApi(
            `/v0/teams/${teamUuid}/files/deleted`,
            { method: 'GET' },
            ApiSchemas['/v0/teams/:uuid/files/deleted.GET.response']
          );
        },
      },
    },
    invites: {
      create(uuid: string, body: ApiTypes['/v0/teams/:uuid/invites.POST.request']) {
        return fetchFromApi(
          `/v0/teams/${uuid}/invites`,
          {
            method: 'POST',
            body: JSON.stringify(body),
          },
          ApiSchemas['/v0/teams/:uuid/invites.POST.response']
        );
      },
      delete(uuid: string, inviteId: string) {
        return fetchFromApi(
          `/v0/teams/${uuid}/invites/${inviteId}`,
          {
            method: 'DELETE',
          },
          ApiSchemas['/v0/teams/:uuid/invites/:inviteId.DELETE.response']
        );
      },
    },
    users: {
      update(uuid: string, userId: string, body: ApiTypes['/v0/teams/:uuid/users/:userId.PATCH.request']) {
        return fetchFromApi(
          `/v0/teams/${uuid}/users/${userId}`,
          { method: 'PATCH', body: JSON.stringify(body) },
          ApiSchemas['/v0/teams/:uuid/users/:userId.PATCH.response']
        );
      },
      delete(uuid: string, userId: string) {
        return fetchFromApi(
          `/v0/teams/${uuid}/users/${userId}`,
          { method: 'DELETE' },
          ApiSchemas['/v0/teams/:uuid/users/:userId.DELETE.response']
        );
      },
    },
    fileLimit(uuid: string) {
      return fetchFromApi(
        `/v0/teams/${uuid}/file-limit`,
        { method: 'GET' },
        ApiSchemas['/v0/teams/:uuid/file-limit.GET.response']
      );
    },
    join(token: string) {
      return fetchFromApi(
        `/v0/teams/join/${token}`,
        { method: 'POST' },
        ApiSchemas['/v0/teams/join/:token.POST.response']
      );
    },
  },

  billing: {
    config() {
      return fetchFromApi(`/v0/billing/config`, { method: 'GET' }, ApiSchemas['/v0/billing/config.GET.response']);
    },
  },

  files: {
    async get(uuid: string, options?: { updateBilling?: boolean; signal?: AbortSignal; skipAuthRedirect?: boolean }) {
      const queryParams = new URLSearchParams();
      if (options?.updateBilling) {
        queryParams.set('updateBilling', 'true');
      }
      const queryString = queryParams.toString();
      const url = `/v0/files/${uuid}${queryString ? `?${queryString}` : ''}`;
      let response = await fetchFromApi(
        url,
        { method: 'GET', signal: options?.signal, skipAuthRedirect: options?.skipAuthRedirect },
        ApiSchemas['/v0/files/:uuid.GET.response']
      );

      if (response.license.status === 'revoked') {
        throw new ApiError('License Revoked', 402, undefined);
      }

      return response;
    },
    create({
      file,
      teamUuid,
      isPrivate,
      folderUuid,
      abortController,
      onUploadProgress,
    }: {
      // TODO(ddimaria): remove Partial and "contents" once we duplicate directly on S3
      file: Partial<Pick<ApiTypes['/v0/files.POST.request'], 'name' | 'contents' | 'version'>>;
      teamUuid: ApiTypes['/v0/files.POST.request']['teamUuid'];
      isPrivate: ApiTypes['/v0/files.POST.request']['isPrivate'];
      folderUuid?: ApiTypes['/v0/files.POST.request']['folderUuid'];
      abortController?: AbortController;
      onUploadProgress?: (uploadProgress: number) => void;
    }) {
      return xhrFromApi(
        `/v0/files`,
        {
          method: 'POST',
          data: { ...file, teamUuid, isPrivate, folderUuid },
          abortController,
          onUploadProgress,
        },
        ApiSchemas['/v0/files.POST.response']
      );
    },

    delete(uuid: string) {
      trackEvent('[Files].deleteFile', { id: uuid });
      return fetchFromApi(`/v0/files/${uuid}`, { method: 'DELETE' }, ApiSchemas['/v0/files/:uuid.DELETE.response']);
    },

    cellHistory(uuid: string, args: { sheetId: string; x: number; y: number; limit?: number }) {
      const queryParams = new URLSearchParams({
        sheetId: args.sheetId,
        x: args.x.toString(),
        y: args.y.toString(),
      });
      if (args.limit) {
        queryParams.set('limit', args.limit.toString());
      }
      return fetchFromApi(
        `/v0/files/${uuid}/cell-history?${queryParams.toString()}`,
        { method: 'GET' },
        ApiSchemas['/v0/files/:uuid/cell-history.GET.response']
      );
    },

    restore(fileUuid: string) {
      return fetchFromApi(
        `/v0/files/${fileUuid}/restore`,
        { method: 'POST' },
        ApiSchemas['/v0/files/:uuid/restore.POST.response']
      );
    },

    async download(uuid: string, args: { checkpointDataUrl?: string } = {}) {
      // Get file info from the server
      const { file } = await this.get(uuid);
      const name = file.name;
      const checkpointDataUrl = args.checkpointDataUrl ?? file.lastCheckpointDataUrl;

      // Download the file from the server, then save it through the browser
      const checkpointData = await fetch(checkpointDataUrl).then((res) => res.arrayBuffer());
      downloadQuadraticFile(name, new Uint8Array(checkpointData));
    },

    async duplicate(
      uuid: string,
      args: {
        teamUuid: string;
        isPrivate: boolean;
        checkpoint?: { dataUrl: string; version: string };
        folderUuid?: string;
      }
    ) {
      // Get the file we want to duplicate
      const {
        file: { name, lastCheckpointDataUrl, lastCheckpointVersion, thumbnail },
      } = await apiClient.files.get(uuid);

      // Get the file checkpoint we’re downloading (the latest if not specified)
      const checkpointVersion = args.checkpoint ? args.checkpoint.version : lastCheckpointVersion;
      const checkpointDataUrl = args.checkpoint ? args.checkpoint.dataUrl : lastCheckpointDataUrl;
      const lastCheckpointContents = await fetch(checkpointDataUrl).then((res) => res.arrayBuffer());
      const buffer = new Uint8Array(lastCheckpointContents);
      const contents = Buffer.from(new Uint8Array(buffer)).toString('base64');

      // Create file on the server. Preserve the source file's folder
      // membership when the caller provided one (e.g. duplicating from the
      // dashboard list while inside a folder) so the copy lands in the same
      // folder as the original.
      const {
        file: { uuid: newFileUuid },
      } = await apiClient.files.create({
        file: {
          name: name + ' (Copy)',
          version: checkpointVersion,
          contents,
        },
        teamUuid: args.teamUuid,
        isPrivate: args.isPrivate,
        folderUuid: args.folderUuid,
      });

      // If we duplicated the latest checkpoint of the file, we'll copy its
      // thumbnail to the file we just created
      if (!args.checkpoint && thumbnail) {
        try {
          const res = await fetch(thumbnail);
          const blob = await res.blob();
          await apiClient.files.thumbnail.update(newFileUuid, blob);
        } catch (err) {
          // Not a huge deal if it failed, just tell Sentry and move on
          captureEvent({
            message: 'Failed to duplicate the thumbnail image when duplicating a file',
            level: 'info',
          });
        }
      }

      return { uuid: newFileUuid };
    },

    update(uuid: string, body: ApiTypes['/v0/files/:uuid.PATCH.request']) {
      return fetchFromApi(
        `/v0/files/${uuid}`,
        {
          method: 'PATCH',
          body: JSON.stringify(body),
        },
        ApiSchemas['/v0/files/:uuid.PATCH.response']
      );
    },

    checkpoints: {
      list(uuid: string) {
        return fetchFromApi(
          `/v0/files/${uuid}/checkpoints`,
          { method: 'GET' },
          ApiSchemas['/v0/files/:uuid/checkpoints.GET.response']
        );
      },
      getBySequenceNumber(uuid: string, sequenceNumber: number, options?: { signal?: AbortSignal }) {
        return fetchFromApi(
          `/v0/files/${uuid}/checkpoints/sequences/${sequenceNumber}`,
          { method: 'GET', signal: options?.signal },
          ApiSchemas['/v0/files/:uuid/checkpoints/sequences/:sequenceNumber.GET.response']
        );
      },
    },

    thumbnail: {
      update(uuid: string, thumbnail: Blob) {
        const formData = new FormData();
        formData.append('thumbnail', thumbnail, 'thumbnail.png');

        return fetchFromApi(
          `/v0/files/${uuid}/thumbnail`,
          {
            method: 'POST',
            body: formData,
          },
          ApiSchemas['/v0/files/:uuid/thumbnail.POST.response']
        );
      },
    },

    sharing: {
      get(uuid: string) {
        return fetchFromApi(
          `/v0/files/${uuid}/sharing`,
          {
            method: 'GET',
          },
          ApiSchemas['/v0/files/:uuid/sharing.GET.response']
        );
      },
      update(uuid: string, body: ApiTypes['/v0/files/:uuid/sharing.PATCH.request']) {
        trackEvent('[FileSharing].publicLinkAccess.update', { value: body.publicLinkAccess });
        return fetchFromApi(
          `/v0/files/${uuid}/sharing`,
          {
            method: 'PATCH',
            body: JSON.stringify(body),
          },
          ApiSchemas['/v0/files/:uuid/sharing.PATCH.response']
        );
      },
    },

    invites: {
      create(uuid: string, body: ApiTypes['/v0/files/:uuid/invites.POST.request']) {
        trackEvent('[FileSharing].invite.create');
        return fetchFromApi(
          `/v0/files/${uuid}/invites`,
          {
            method: 'POST',
            body: JSON.stringify(body),
          },
          ApiSchemas['/v0/files/:uuid/invites.POST.response']
        );
      },
      delete(uuid: string, inviteId: string) {
        trackEvent('[FileSharing].invite.delete');
        return fetchFromApi(
          `/v0/files/${uuid}/invites/${inviteId}`,
          {
            method: 'DELETE',
          },
          ApiSchemas['/v0/files/:uuid/invites/:inviteId.DELETE.response']
        );
      },
    },

    users: {
      update(uuid: string, userId: string, body: ApiTypes['/v0/files/:uuid/users/:userId.PATCH.request']) {
        trackEvent('[FileSharing].users.updateRole');
        return fetchFromApi(
          `/v0/files/${uuid}/users/${userId}`,
          { method: 'PATCH', body: JSON.stringify(body) },
          ApiSchemas['/v0/files/:uuid/users/:userId.PATCH.response']
        );
      },
      delete(uuid: string, userId: string) {
        trackEvent('[FileSharing].users.remove');
        return fetchFromApi(
          `/v0/files/${uuid}/users/${userId}`,
          { method: 'DELETE' },
          ApiSchemas['/v0/files/:uuid/users/:userId.DELETE.response']
        );
      },
    },
  },

  folders: {
    create(body: ApiTypes['/v0/folders.POST.request']) {
      return fetchFromApi(
        `/v0/folders`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/folders.POST.response']
      );
    },
    update(uuid: string, body: ApiTypes['/v0/folders/:uuid.PATCH.request']) {
      return fetchFromApi(
        `/v0/folders/${uuid}`,
        { method: 'PATCH', body: JSON.stringify(body) },
        ApiSchemas['/v0/folders/:uuid.PATCH.response']
      );
    },
    delete(uuid: string) {
      return fetchFromApi(`/v0/folders/${uuid}`, { method: 'DELETE' }, ApiSchemas['/v0/folders/:uuid.DELETE.response']);
    },
    getDeletePreview(uuid: string) {
      return fetchFromApi(
        `/v0/folders/${uuid}/delete-preview`,
        { method: 'GET' },
        ApiSchemas['/v0/folders/:uuid/delete-preview.GET.response']
      );
    },
  },

  templates: {
    async duplicate(body: ApiTypes['/v0/examples.POST.request']) {
      const { uuid: newFileUuid, name } = await fetchFromApi(
        `/v0/examples`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/examples.POST.response']
      );

      // Copy the thumbnail from the production template to the new file
      try {
        const fileUuid = body.publicFileUrlInProduction.split('/').pop();
        const prodApiUrl = `https://api-aws.quadratichq.com/v0/files/${fileUuid}`;
        const prodFile: ApiTypes['/v0/files/:uuid.GET.response'] = await fetch(prodApiUrl).then((res) => res.json());

        if (prodFile.file?.thumbnail) {
          const thumbnailBlob = await fetch(prodFile.file.thumbnail).then((res) => res.blob());
          await apiClient.files.thumbnail.update(newFileUuid, thumbnailBlob);
        }
      } catch (err) {
        captureEvent({
          message: 'Failed to copy thumbnail from template',
          level: 'info',
        });
      }

      return { uuid: newFileUuid, name };
    },
  },

  user: {
    acknowledge() {
      return fetchFromApi(`/v0/user/acknowledge`, { method: 'GET' }, ApiSchemas['/v0/user/acknowledge.GET.response']);
    },
    update(body: ApiTypes['/v0/user.POST.request']) {
      return fetchFromApi(
        `/v0/user`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/user.POST.response']
      );
    },
    clientDataKv: {
      get() {
        return fetchFromApi(
          `/v0/user/client-data-kv`,
          { method: 'GET' },
          ApiSchemas['/v0/user/client-data-kv.GET.response']
        );
      },
      update(body: ApiTypes['/v0/user/client-data-kv.POST.request']) {
        return fetchFromApi(
          `/v0/user/client-data-kv`,
          { method: 'POST', body: JSON.stringify(body) },
          ApiSchemas['/v0/user/client-data-kv.POST.response']
        );
      },
    },
    aiLanguages: {
      get() {
        return fetchFromApi(
          `/v0/user/ai-languages`,
          { method: 'GET' },
          ApiSchemas['/v0/user/ai-languages.GET.response']
        );
      },
      update(body: ApiTypes['/v0/user/ai-languages.PATCH.request']) {
        return fetchFromApi(
          `/v0/user/ai-languages`,
          { method: 'PATCH', body: JSON.stringify(body) },
          ApiSchemas['/v0/user/ai-languages.PATCH.response']
        );
      },
    },
  },

  education: {
    get() {
      return fetchFromApi(`/v0/education`, { method: 'GET' }, ApiSchemas['/v0/education.GET.response']);
    },
    refresh() {
      return fetchFromApi(`/v0/education`, { method: 'POST' }, ApiSchemas['/v0/education.POST.response']);
    },
  },

  connections: {
    list(teamUuid: string) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/connections`,
        { method: 'GET' },
        ApiSchemas['/v0/teams/:uuid/connections.GET.response']
      );
    },
    get({ connectionUuid, teamUuid }: { connectionUuid: string; teamUuid: string }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/connections/${connectionUuid}`,
        { method: 'GET' },
        ApiSchemas['/v0/teams/:uuid/connections/:connectionUuid.GET.response']
      );
    },
    create({ body, teamUuid }: { body: ApiTypes['/v0/teams/:uuid/connections.POST.request']; teamUuid: string }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/connections`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid/connections.POST.response']
      );
    },
    update({
      connectionUuid,
      body,
      teamUuid,
    }: {
      connectionUuid: string;
      body: ApiTypes['/v0/teams/:uuid/connections/:connectionUuid.PUT.request'];
      teamUuid: string;
    }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/connections/${connectionUuid}`,
        { method: 'PUT', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid/connections/:connectionUuid.PUT.response']
      );
    },
    delete({ connectionUuid, teamUuid }: { connectionUuid: string; teamUuid: string }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/connections/${connectionUuid}`,
        { method: 'DELETE' },
        ApiSchemas['/v0/teams/:uuid/connections/:connectionUuid.DELETE.response']
      );
    },
    getLogs({
      connectionUuid,
      teamUuid,
      pageNumber = 1,
      pageSize = 10,
    }: {
      connectionUuid: string;
      teamUuid: string;
      pageNumber?: number;
      pageSize?: number;
    }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/connections/${connectionUuid}/log?page=${pageNumber}&limit=${pageSize}`,
        { method: 'GET' },
        ApiSchemas['/v0/teams/:uuid/connections/:connectionUuid/log.GET.response']
      );
    },
    plaid: {
      createLinkToken({
        teamUuid,
        primary_product,
        connection_uuid,
      }: {
        teamUuid: string;
        primary_product?: 'transactions' | 'investments' | 'liabilities';
        connection_uuid?: string;
      }) {
        return fetchFromApi(
          `/v0/teams/${teamUuid}/plaid/link-token`,
          {
            method: 'POST',
            body: JSON.stringify({ primary_product, connection_uuid }),
          },
          ApiSchemas['/v0/teams/:uuid/plaid/link-token.POST.response']
        );
      },
      exchangeToken({ teamUuid, publicToken }: { teamUuid: string; publicToken: string }) {
        return fetchFromApi(
          `/v0/teams/${teamUuid}/plaid/exchange-token`,
          {
            method: 'POST',
            body: JSON.stringify({ publicToken }),
          },
          ApiSchemas['/v0/teams/:uuid/plaid/exchange-token.POST.response']
        );
      },
    },
    google: {
      getAuthUrl({ teamUuid, purpose }: { teamUuid: string; purpose?: 'analytics' | 'sheets' }) {
        const query = purpose ? `?purpose=${purpose}` : '';
        return fetchFromApi(
          `/v0/teams/${teamUuid}/google/auth-url${query}`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/google/auth-url.GET.response']
        );
      },
      exchangeToken({ teamUuid, code, state }: { teamUuid: string; code: string; state: string }) {
        return fetchFromApi(
          `/v0/teams/${teamUuid}/google/exchange-token`,
          {
            method: 'POST',
            body: JSON.stringify({ code, state }),
          },
          ApiSchemas['/v0/teams/:uuid/google/exchange-token.POST.response']
        );
      },
      downloadSheet({
        teamUuid,
        accessToken,
        spreadsheetUrl,
      }: {
        teamUuid: string;
        accessToken: string;
        spreadsheetUrl: string;
      }) {
        return fetchFromApi(
          `/v0/teams/${teamUuid}/google/sheets/download`,
          {
            method: 'POST',
            body: JSON.stringify({ accessToken, spreadsheetUrl }),
          },
          ApiSchemas['/v0/teams/:uuid/google/sheets/download.POST.response']
        );
      },
    },
    quickbooks: {
      getAuthUrl({ teamUuid }: { teamUuid: string }) {
        return fetchFromApi(
          `/v0/teams/${teamUuid}/quickbooks/auth-url`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/quickbooks/auth-url.GET.response']
        );
      },
      exchangeToken({
        teamUuid,
        code,
        state,
        realmId,
      }: {
        teamUuid: string;
        code: string;
        state: string;
        realmId: string;
      }) {
        return fetchFromApi(
          `/v0/teams/${teamUuid}/quickbooks/exchange-token`,
          {
            method: 'POST',
            body: JSON.stringify({ code, state, realmId }),
          },
          ApiSchemas['/v0/teams/:uuid/quickbooks/exchange-token.POST.response']
        );
      },
    },
  },

  context: {
    team: {
      get(teamUuid: string) {
        return fetchFromApi(
          `/v0/teams/${teamUuid}/context`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/context.GET.response']
        );
      },
      update({ teamUuid, body }: { teamUuid: string; body: ApiTypes['/v0/teams/:uuid/context.PUT.request'] }) {
        return fetchFromApi(
          `/v0/teams/${teamUuid}/context`,
          { method: 'PUT', body: JSON.stringify(body) },
          ApiSchemas['/v0/teams/:uuid/context.PUT.response']
        );
      },
      versions({ teamUuid, limit, offset }: { teamUuid: string; limit?: number; offset?: number }) {
        const qs = new URLSearchParams();
        if (limit !== undefined) qs.set('limit', String(limit));
        if (offset !== undefined) qs.set('offset', String(offset));
        const query = qs.toString();
        return fetchFromApi(
          `/v0/teams/${teamUuid}/context/versions${query ? `?${query}` : ''}`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/context/versions.GET.response']
        );
      },
      version({ teamUuid, version }: { teamUuid: string; version: number }) {
        return fetchFromApi(
          `/v0/teams/${teamUuid}/context/versions/${version}`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/context/versions/:version.GET.response']
        );
      },
    },
    user: {
      get() {
        return fetchFromApi(`/v0/user/context`, { method: 'GET' }, ApiSchemas['/v0/user/context.GET.response']);
      },
      update(body: ApiTypes['/v0/user/context.PUT.request']) {
        return fetchFromApi(
          `/v0/user/context`,
          { method: 'PUT', body: JSON.stringify(body) },
          ApiSchemas['/v0/user/context.PUT.response']
        );
      },
      versions(params?: { limit?: number; offset?: number }) {
        const qs = new URLSearchParams();
        if (params?.limit !== undefined) qs.set('limit', String(params.limit));
        if (params?.offset !== undefined) qs.set('offset', String(params.offset));
        const query = qs.toString();
        return fetchFromApi(
          `/v0/user/context/versions${query ? `?${query}` : ''}`,
          { method: 'GET' },
          ApiSchemas['/v0/user/context/versions.GET.response']
        );
      },
      version({ version }: { version: number }) {
        return fetchFromApi(
          `/v0/user/context/versions/${version}`,
          { method: 'GET' },
          ApiSchemas['/v0/user/context/versions/:version.GET.response']
        );
      },
    },
    connection: {
      get({ teamUuid, connectionUuid }: { teamUuid: string; connectionUuid: string }) {
        return fetchFromApi(
          `/v0/teams/${teamUuid}/connections/${connectionUuid}/context`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/connections/:connectionUuid/context.GET.response']
        );
      },
      update({
        teamUuid,
        connectionUuid,
        body,
      }: {
        teamUuid: string;
        connectionUuid: string;
        body: ApiTypes['/v0/teams/:uuid/connections/:connectionUuid/context.PUT.request'];
      }) {
        return fetchFromApi(
          `/v0/teams/${teamUuid}/connections/${connectionUuid}/context`,
          { method: 'PUT', body: JSON.stringify(body) },
          ApiSchemas['/v0/teams/:uuid/connections/:connectionUuid/context.PUT.response']
        );
      },
      versions({
        teamUuid,
        connectionUuid,
        limit,
        offset,
      }: {
        teamUuid: string;
        connectionUuid: string;
        limit?: number;
        offset?: number;
      }) {
        const qs = new URLSearchParams();
        if (limit !== undefined) qs.set('limit', String(limit));
        if (offset !== undefined) qs.set('offset', String(offset));
        const query = qs.toString();
        return fetchFromApi(
          `/v0/teams/${teamUuid}/connections/${connectionUuid}/context/versions${query ? `?${query}` : ''}`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/connections/:connectionUuid/context/versions.GET.response']
        );
      },
      version({ teamUuid, connectionUuid, version }: { teamUuid: string; connectionUuid: string; version: number }) {
        return fetchFromApi(
          `/v0/teams/${teamUuid}/connections/${connectionUuid}/context/versions/${version}`,
          { method: 'GET' },
          ApiSchemas['/v0/teams/:uuid/connections/:connectionUuid/context/versions/:version.GET.response']
        );
      },
    },
    // Agent-triggered edit endpoint — used by the `edit_context` tool action.
    agentEdit({
      teamUuid,
      body,
    }: {
      teamUuid: string;
      body: ApiTypes['/v0/teams/:uuid/context/agent-edit.POST.request'];
    }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/context/agent-edit`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid/context/agent-edit.POST.response']
      );
    },
  },

  secrets: {
    list(teamUuid: string) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/secrets`,
        { method: 'GET' },
        ApiSchemas['/v0/teams/:uuid/secrets.GET.response']
      );
    },
    create({ teamUuid, body }: { teamUuid: string; body: ApiTypes['/v0/teams/:uuid/secrets.POST.request'] }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/secrets`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid/secrets.POST.response']
      );
    },
    update({
      teamUuid,
      secretUuid,
      body,
    }: {
      teamUuid: string;
      secretUuid: string;
      body: ApiTypes['/v0/teams/:uuid/secrets/:secretUuid.PUT.request'];
    }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/secrets/${secretUuid}`,
        { method: 'PUT', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid/secrets/:secretUuid.PUT.response']
      );
    },
    delete({ teamUuid, secretUuid }: { teamUuid: string; secretUuid: string }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/secrets/${secretUuid}`,
        { method: 'DELETE' },
        ApiSchemas['/v0/teams/:uuid/secrets/:secretUuid.DELETE.response']
      );
    },
  },

  agentConnections: {
    list(teamUuid: string) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/agent-connections`,
        { method: 'GET' },
        ApiSchemas['/v0/teams/:uuid/agent-connections.GET.response']
      );
    },
    get({ teamUuid, connectionUuid }: { teamUuid: string; connectionUuid: string }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/agent-connections/${connectionUuid}`,
        { method: 'GET' },
        ApiSchemas['/v0/teams/:uuid/agent-connections/:connectionUuid.GET.response']
      );
    },
    create({ teamUuid, body }: { teamUuid: string; body: ApiTypes['/v0/teams/:uuid/agent-connections.POST.request'] }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/agent-connections`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid/agent-connections.POST.response']
      );
    },
    update({
      teamUuid,
      connectionUuid,
      body,
    }: {
      teamUuid: string;
      connectionUuid: string;
      body: ApiTypes['/v0/teams/:uuid/agent-connections/:connectionUuid.PUT.request'];
    }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/agent-connections/${connectionUuid}`,
        { method: 'PUT', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid/agent-connections/:connectionUuid.PUT.response']
      );
    },
    delete({ teamUuid, connectionUuid }: { teamUuid: string; connectionUuid: string }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/agent-connections/${connectionUuid}`,
        { method: 'DELETE' },
        ApiSchemas['/v0/teams/:uuid/agent-connections/:connectionUuid.DELETE.response']
      );
    },
    uploadThumbnail({ teamUuid, connectionUuid, file }: { teamUuid: string; connectionUuid: string; file: File }) {
      const formData = new FormData();
      formData.append('thumbnail', file, file.name);
      return fetchFromApi(
        `/v0/teams/${teamUuid}/agent-connections/${connectionUuid}/thumbnail`,
        { method: 'POST', body: formData },
        ApiSchemas['/v0/teams/:uuid/agent-connections/:connectionUuid/thumbnail.POST.response']
      );
    },
    bindSecret({
      teamUuid,
      connectionUuid,
      body,
    }: {
      teamUuid: string;
      connectionUuid: string;
      body: ApiTypes['/v0/teams/:uuid/agent-connections/:connectionUuid/secret-bindings.POST.request'];
    }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/agent-connections/${connectionUuid}/secret-bindings`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid/agent-connections/:connectionUuid/secret-bindings.POST.response']
      );
    },
    unbindSecret({
      teamUuid,
      connectionUuid,
      bindingId,
    }: {
      teamUuid: string;
      connectionUuid: string;
      bindingId: number;
    }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/agent-connections/${connectionUuid}/secret-bindings/${bindingId}`,
        { method: 'DELETE' },
        ApiSchemas['/v0/teams/:uuid/agent-connections/:connectionUuid/secret-bindings/:bindingId.DELETE.response']
      );
    },
    test({
      teamUuid,
      connectionUuid,
      body,
    }: {
      teamUuid: string;
      connectionUuid: string;
      body: ApiTypes['/v0/teams/:uuid/agent-connections/:connectionUuid/test.POST.request'];
    }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/agent-connections/${connectionUuid}/test`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid/agent-connections/:connectionUuid/test.POST.response']
      );
    },
    oauthAuthUrl({ teamUuid, connectionUuid }: { teamUuid: string; connectionUuid: string }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/agent-connections/${connectionUuid}/oauth/auth-url`,
        { method: 'POST' },
        ApiSchemas['/v0/teams/:uuid/agent-connections/:connectionUuid/oauth/auth-url.POST.response']
      );
    },
    oauthExchange({
      teamUuid,
      connectionUuid,
      body,
    }: {
      teamUuid: string;
      connectionUuid: string;
      body: ApiTypes['/v0/teams/:uuid/agent-connections/:connectionUuid/oauth/exchange.POST.request'];
    }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/agent-connections/${connectionUuid}/oauth/exchange`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid/agent-connections/:connectionUuid/oauth/exchange.POST.response']
      );
    },
  },

  agentCatalog: {
    list() {
      return fetchFromApi(`/v0/agent-catalog`, { method: 'GET' }, ApiSchemas['/v0/agent-catalog.GET.response']);
    },
    /**
     * Submit a finished custom AgentConnection to the admin review queue.
     * Server reads the plan + apiSchema off the AgentConnection row by
     * uuid; dedupes against any in-flight or approved proposal for the
     * same service so repeat submissions are silent no-ops.
     */
    submitProposal(body: ApiTypes['/v0/agent-catalog/proposals.POST.request']) {
      return fetchFromApi(
        `/v0/agent-catalog/proposals`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/agent-catalog/proposals.POST.response']
      );
    },
  },

  // Quadratic-admin-only catalog management. Surfaced from the admin UI
  // alongside the existing knowledge admin tools. All routes return 403 for
  // non-admin callers.
  agentCatalogAdmin: {
    listEntries() {
      return fetchFromApi(
        `/v0/admin/agent-catalog/entries`,
        { method: 'GET' },
        ApiSchemas['/v0/admin/agent-catalog/entries.GET.response']
      );
    },
    updateEntry({ uuid, body }: { uuid: string; body: ApiTypes['/v0/admin/agent-catalog/entries/:uuid.PUT.request'] }) {
      return fetchFromApi(
        `/v0/admin/agent-catalog/entries/${uuid}`,
        { method: 'PUT', body: JSON.stringify(body) },
        ApiSchemas['/v0/admin/agent-catalog/entries/:uuid.PUT.response']
      );
    },
    deleteEntry({ uuid }: { uuid: string }) {
      return fetchFromApi(
        `/v0/admin/agent-catalog/entries/${uuid}`,
        { method: 'DELETE' },
        ApiSchemas['/v0/admin/agent-catalog/entries/:uuid.DELETE.response']
      );
    },
    uploadEntryThumbnail({ uuid, file }: { uuid: string; file: File }) {
      const formData = new FormData();
      formData.append('thumbnail', file, file.name);
      return fetchFromApi(
        `/v0/admin/agent-catalog/entries/${uuid}/thumbnail`,
        { method: 'POST', body: formData },
        ApiSchemas['/v0/admin/agent-catalog/entries/:uuid/thumbnail.POST.response']
      );
    },
    listProposals() {
      return fetchFromApi(
        `/v0/admin/agent-catalog/proposals`,
        { method: 'GET' },
        ApiSchemas['/v0/admin/agent-catalog/proposals.GET.response']
      );
    },
    createProposal(body: ApiTypes['/v0/admin/agent-catalog/proposals.POST.request']) {
      return fetchFromApi(
        `/v0/admin/agent-catalog/proposals`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/admin/agent-catalog/proposals.POST.response']
      );
    },
    getProposal(uuid: string) {
      return fetchFromApi(
        `/v0/admin/agent-catalog/proposals/${uuid}`,
        { method: 'GET' },
        ApiSchemas['/v0/admin/agent-catalog/proposals/:uuid.GET.response']
      );
    },
    completeProposal({
      uuid,
      body,
    }: {
      uuid: string;
      body: ApiTypes['/v0/admin/agent-catalog/proposals/:uuid/complete.POST.request'];
    }) {
      return fetchFromApi(
        `/v0/admin/agent-catalog/proposals/${uuid}/complete`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/admin/agent-catalog/proposals/:uuid/complete.POST.response']
      );
    },
    approveProposal({
      uuid,
      body,
    }: {
      uuid: string;
      body: ApiTypes['/v0/admin/agent-catalog/proposals/:uuid/approve.POST.request'];
    }) {
      return fetchFromApi(
        `/v0/admin/agent-catalog/proposals/${uuid}/approve`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/admin/agent-catalog/proposals/:uuid/approve.POST.response']
      );
    },
    rejectProposal({
      uuid,
      body,
    }: {
      uuid: string;
      body: ApiTypes['/v0/admin/agent-catalog/proposals/:uuid/reject.POST.request'];
    }) {
      return fetchFromApi(
        `/v0/admin/agent-catalog/proposals/${uuid}/reject`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/admin/agent-catalog/proposals/:uuid/reject.POST.response']
      );
    },
    uploadProposalThumbnail({ uuid, file }: { uuid: string; file: File }) {
      const formData = new FormData();
      formData.append('thumbnail', file, file.name);
      return fetchFromApi(
        `/v0/admin/agent-catalog/proposals/${uuid}/thumbnail`,
        { method: 'POST', body: formData },
        ApiSchemas['/v0/admin/agent-catalog/proposals/:uuid/thumbnail.POST.response']
      );
    },
  },

  apiTokens: {
    list(teamUuid: string) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/api-tokens`,
        { method: 'GET' },
        ApiSchemas['/v0/teams/:uuid/api-tokens.GET.response']
      );
    },
    create({ teamUuid, body }: { teamUuid: string; body: ApiTypes['/v0/teams/:uuid/api-tokens.POST.request'] }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/api-tokens`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid/api-tokens.POST.response']
      );
    },
    rename({
      teamUuid,
      tokenUuid,
      body,
    }: {
      teamUuid: string;
      tokenUuid: string;
      body: ApiTypes['/v0/teams/:uuid/api-tokens/:tokenUuid.PATCH.request'];
    }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/api-tokens/${tokenUuid}`,
        { method: 'PATCH', body: JSON.stringify(body) },
        ApiSchemas['/v0/teams/:uuid/api-tokens/:tokenUuid.PATCH.response']
      );
    },
    revoke({ teamUuid, tokenUuid }: { teamUuid: string; tokenUuid: string }) {
      return fetchFromApi(
        `/v0/teams/${teamUuid}/api-tokens/${tokenUuid}`,
        { method: 'DELETE' },
        ApiSchemas['/v0/teams/:uuid/api-tokens/:tokenUuid.DELETE.response']
      );
    },
  },

  ai: {
    feedback(body: ApiTypes['/v0/ai/feedback.PATCH.request']) {
      return fetchFromApi(
        `/v0/ai/feedback`,
        { method: 'PATCH', body: JSON.stringify(body) },
        ApiSchemas['/v0/ai/feedback.PATCH.response']
      );
    },
    codeRunError(body: ApiTypes['/v0/ai/codeRunError.PATCH.request']) {
      return fetchFromApi(
        `/v0/ai/codeRunError`,
        { method: 'PATCH', body: JSON.stringify(body) },
        ApiSchemas['/v0/ai/codeRunError.PATCH.response']
      );
    },
  },

  embeds: {
    get(embedUuid: string) {
      return fetchFromApi(`/v0/embeds/${embedUuid}`, { method: 'GET' }, ApiSchemas['/v0/embeds/:uuid.GET.response']);
    },
    create(fileUuid: string) {
      return fetchFromApi(
        `/v0/files/${fileUuid}/embeds`,
        { method: 'POST' },
        ApiSchemas['/v0/files/:uuid/embeds.POST.response']
      );
    },
    list(fileUuid: string) {
      return fetchFromApi(
        `/v0/files/${fileUuid}/embeds`,
        { method: 'GET' },
        ApiSchemas['/v0/files/:uuid/embeds.GET.response']
      );
    },
    delete(embedUuid: string) {
      return fetchFromApi(
        `/v0/embeds/${embedUuid}`,
        { method: 'DELETE' },
        ApiSchemas['/v0/embeds/:uuid.DELETE.response']
      );
    },
  },

  embed: {
    uploadRequest(body: ApiTypes['/v0/embed/upload-request.POST.request']) {
      return fetchFromApi(
        `/v0/embed/upload-request`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/embed/upload-request.POST.response']
      );
    },
    claim(body: ApiTypes['/v0/embed/claim.POST.request']) {
      return fetchFromApi(
        `/v0/embed/claim`,
        { method: 'POST', body: JSON.stringify(body) },
        ApiSchemas['/v0/embed/claim.POST.response']
      );
    },
  },

  postFeedback(body: ApiTypes['/v0/feedback.POST.request']) {
    return fetchFromApi(
      `/v0/feedback`,
      { method: 'POST', body: JSON.stringify(body) },
      ApiSchemas['/v0/feedback.POST.response']
    );
  },

  urlMetadata: {
    get(url: string) {
      return fetchFromApi(
        `/v0/url-metadata?url=${encodeURIComponent(url)}`,
        { method: 'GET' },
        ApiSchemas['/v0/url-metadata.GET.response']
      );
    },
  },

  getApiUrl() {
    const url = import.meta.env.VITE_QUADRATIC_API_URL;
    if (!url) {
      const message = 'VITE_QUADRATIC_API_URL env variable is not set.';
      captureEvent({
        message,
        level: 'fatal',
      });
      throw new Error(message);
    }

    return url;
  },
};
